import * as React from "react";
import { SimilarDestinationsCardProps, SimilarDestinationsProps } from "./typings";
import { UitkCarousel } from "uitk-react-carousel";
import { AnalyticsConfig } from "uitk-react-utils";
import { UitkCard, UitkCardLink, UitkCardMedia } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from "uitk-react-images";
import { SimilarDestination } from "typings/microserviceModels/te-similar-destination-flex-module";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { UitkScrim } from "uitk-react-scrim";
import { ViewLarge, Viewport, ViewSmall } from "bernie-view";
import { AspectRatioEnum } from "src/stores/thumbor/ThumborStore";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { inject } from "mobx-react";
import { MediaAttribution } from "components/utility/MediaAttributionUtil";
import { formatText } from "bernie-l10n";
import {
  Action,
  FlexTrackingInfo,
  sendDelayedTrackEvent
} from "components/utility/analytics/FlexAnalyticsUtils";
import LazyLoad from "react-lazyload";

@inject("thumbor", "analytics")
export class SimilarDestinations extends React.Component<SimilarDestinationsProps> {
  private safelyResizeImage(destination: SimilarDestination) {
    const image: string = destination.media && destination.media.mediaUrl;

    return image && this.props.thumbor
      ? this.props.thumbor.safelyGenerateThumborizedImageUrl(
          image,
          330,
          190,
          this.props.context,
          AspectRatioEnum.DEFAULT,
          true,
          100
        )
      : image;
  }

  public render() {
    const destinations =
      this.props.flexComponent.model &&
      this.props.flexComponent.model.content &&
      !this.props.flexComponent.model.empty &&
      this.props.flexComponent.model.content.items;
    if (!destinations || !destinations.length) {
      return null;
    }

    const destinationKeyHelper = new ItemKeyHelper("similar-destinations");

    const context = this.props.context;

    const analytics: AnalyticsConfig = {
      id: "TG.LP.Dest.OtherPlaces",
      callback: (id, description) => {
        const flexTrackingInfo: FlexTrackingInfo = {
          moduleName: id,
          action: Action.SCROLL,
          linkName: description
        };
        sendDelayedTrackEvent(flexTrackingInfo, this.props.analytics);
      }
    };

    const localisedHeading = `${formatText(
      "similarDestinations.title",
      this.props.context.searchContext.location.name
    )}`;

    const carouselProps = {
      itemsVisible: { lg: 1, md: 1, sm: 1 },
      pageBy: 1,
      peekWidth: 25,
      buttonText: {
        nextButton: formatText("carousel.item.next"),
        prevButton: formatText("carousel.item.prev")
      }
    };

    return (
      <Viewport>
        <ViewSmall>
          <div
            className="SimilarDestinations SimilarDestinations--carousel uitk-cell all-cell-1-1 all-b-margin-six"
            data-fm={this.props.flexComponent.fmId}
            data-fm-title-id={this.props.flexComponent.fmTitleId}
          >
            <UitkCarousel
              headline={localisedHeading}
              analytics={analytics}
              carouselName={"SimilarDestinations"}
              {...carouselProps}
            >
              {destinations.map((destination, index) => (
                <SimilarDestinationsCard
                  key={destinationKeyHelper.next()}
                  destination={destination}
                  context={context}
                  position={index + 1}
                  destinationImageUrl={this.safelyResizeImage(destination)}
                  className="uitk-carousel-item"
                  scrimClassName="uitk-type-display-700"
                />
              ))}
            </UitkCarousel>
          </div>
        </ViewSmall>
        <ViewLarge>
          <div
            className="SimilarDestinations SimilarDestinations--desktop uitk-cell all-cell-1-1 all-b-margin-six"
            data-fm={this.props.flexComponent.fmId}
          >
            <h3 className="uitk-type-heading-600 all-b-margin-three">{localisedHeading}</h3>
            <div className="uitk-grid all-cell-1-4 all-x-gutter-three">
              {destinations.map((destination, index) => (
                <div className="uitk-cell" key={destinationKeyHelper.next()}>
                  <SimilarDestinationsCard
                    key={destinationKeyHelper.next()}
                    destination={destination}
                    context={context}
                    position={index + 1}
                    destinationImageUrl={this.safelyResizeImage(destination)}
                    className=""
                    scrimClassName="uitk-type-heading-600"
                  />
                </div>
              ))}
            </div>
          </div>
        </ViewLarge>
      </Viewport>
    );
  }
}

export const SimilarDestinationsCard = ({
  destination,
  position,
  destinationImageUrl,
  className,
  scrimClassName
}: SimilarDestinationsCardProps) => {
  const { media, link, localisedRegionName } = destination;

  const placeholder = (
    <UitkImage
      alt={formatText("hotels.imagePlaceHolder", localisedRegionName)}
      placeholderImage={true}
    />
  );

  const attribution = MediaAttribution(media, localisedRegionName);

  return (
    <UitkCard className={className} key={position}>
      <UitkCardMedia>
        <UitkCardLink>
          <TrackedLink
            moduleName="TG.LP"
            href={link}
            rfrr="Dest.OtherPlaces"
            linkName={localisedRegionName}
          >
            {localisedRegionName}
          </TrackedLink>
        </UitkCardLink>
        <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
          <LazyLoad placeholder={placeholder} offset={250}>
            <UitkImage
              alt={attribution && attribution.attributionAltText}
              src={destinationImageUrl}
            />
          </LazyLoad>
          <UitkScrim className={scrimClassName}>{localisedRegionName}</UitkScrim>
          {attribution && attribution.attributionText}
        </UitkFigure>
      </UitkCardMedia>
    </UitkCard>
  );
};

export default SimilarDestinations;
